import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import type {RouteObject} from '@github-ui/react-router'
import type {ComponentType} from 'react'

import {buildCodeViewBlameRoute} from './code-view-blame-route'
import {buildCodeViewBlobLayoutRoute} from './code-view-blob-layout-route'
import {buildCodeViewBlobRoute} from './code-view-blob-route'
import {buildCodeViewDeleteRoute} from './code-view-delete-route'
import {buildCodeViewEditRoute} from './code-view-edit-route'
import {buildCodeViewFileTreeLayoutRoute} from './code-view-file-tree-layout-route'
import {buildCodeViewLayoutRoute} from './code-view-layout-route'
import {buildCodeViewNewRoute} from './code-view-new-route'
import {buildCodeViewRepoRoute} from './code-view-repo-route'
import {buildCodeViewTreeDeleteRoute} from './code-view-tree-delete-route'
import {buildCodeViewTreeRoute} from './code-view-tree-route'

export interface CodeViewRouteComponents {
  CodeViewBlameEntrypoint: ComponentType
  CodeViewBlobEntrypoint: ComponentType
  CodeViewBlobLayout: ComponentType
  CodeViewBlobLayoutErrorBoundary: ComponentType
  CodeViewDeleteEntrypoint: ComponentType
  CodeViewEditEntrypoint: ComponentType
  CodeViewFileTreeLayout: ComponentType
  CodeViewLayout: ComponentType
  CodeViewNewEntrypoint: ComponentType
  CodeViewRepoEntrypoint: ComponentType
  CodeViewTreeDeleteEntrypoint: ComponentType
  CodeViewTreeEntrypoint: ComponentType
  CodeViewTreeErrorBoundary: ComponentType
}

/**
 * Builds the Code View route objects for a given app builder.
 *
 * Route configs are bound to the app builder that creates them (embedded-data
 * hydration + query-key app name), so registering these routes under a different
 * app (e.g. `repo`) requires rebuilding them with that app's builder rather than
 * importing the `code-view`-bound singletons directly.
 *
 * Note: the `tanStackRouterEnabled` option is set at the app level (on
 * `createDataRouterAppFromRoutes`), not here, so a consuming app opts into
 * TanStack Router independently of these route definitions.
 */
export function createCodeViewRoutes(
  appBuilder: DataRouterApplicationBuilder<string>,
  components: CodeViewRouteComponents,
): RouteObject[] {
  const {
    CodeViewBlameEntrypoint,
    CodeViewBlobEntrypoint,
    CodeViewBlobLayout,
    CodeViewBlobLayoutErrorBoundary,
    CodeViewDeleteEntrypoint,
    CodeViewEditEntrypoint,
    CodeViewFileTreeLayout,
    CodeViewLayout,
    CodeViewNewEntrypoint,
    CodeViewRepoEntrypoint,
    CodeViewTreeDeleteEntrypoint,
    CodeViewTreeEntrypoint,
    CodeViewTreeErrorBoundary,
  } = components

  return [
    buildCodeViewLayoutRoute(appBuilder).toRoute({
      Component: CodeViewLayout,
      children: [
        buildCodeViewRepoRoute(appBuilder).toRoute({
          Component: CodeViewRepoEntrypoint,
        }),
        buildCodeViewFileTreeLayoutRoute(appBuilder).toRoute({
          Component: CodeViewFileTreeLayout,
          children: [
            // Most specific routes first (more path segments)
            buildCodeViewTreeDeleteRoute(appBuilder).toRoute({
              Component: CodeViewTreeDeleteEntrypoint,
            }),
            buildCodeViewEditRoute(appBuilder).toRoute({
              Component: CodeViewEditEntrypoint,
            }),
            buildCodeViewDeleteRoute(appBuilder).toRoute({
              Component: CodeViewDeleteEntrypoint,
            }),
            // Routes without :path parameter
            buildCodeViewNewRoute(appBuilder).toRoute({
              Component: CodeViewNewEntrypoint,
            }),
            buildCodeViewTreeRoute(appBuilder).toRoute({
              Component: CodeViewTreeEntrypoint,
              errorBoundary: {override: CodeViewTreeErrorBoundary},
            }),
            buildCodeViewBlobLayoutRoute(appBuilder).toRoute({
              Component: CodeViewBlobLayout,
              errorBoundary: {override: CodeViewBlobLayoutErrorBoundary},
              children: [
                buildCodeViewBlobRoute(appBuilder).toRoute({
                  Component: CodeViewBlobEntrypoint,
                }),
                buildCodeViewBlameRoute(appBuilder).toRoute({
                  Component: CodeViewBlameEntrypoint,
                }),
              ],
            }),
          ],
        }),
      ],
    }),
  ]
}
